import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Pencil as EditIcon } from "lucide-react";
import { Snackbar } from "../../../../../components/ui/Feedback";
import {
  addShoppingCartItems,
  getUserByCompanyCode,
} from "../../../../../api/action/user";
import Loading from "../../Enquiry/components/Loading";
import EducationDetails from "../AdditionalInformation/components/EducationDetails";
import ParentDetails from "../AdditionalInformation/components/ParentDetails";
import PersonalInfo from "../AdditionalInformation/components/PersonalInfo";
import BackButton from "./../../components/CustomFields/BackButton";
import BackgroundComponent from "./../../components/theme/BackgroundAnimation";
import InstitutionHeader from "../../components/InstitutionHeader";
import ContactPersonDetails from "./components/ContactPerson";
import AddressListDetails from "./components/AddressListDetails";
import AdditionalInfo from "./components/AdditionalInfo";

const ApplicantForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetail = useSelector((state) => state?.auth?.data);
  const selectedBranch = useSelector((state) => state.selectedBranch);
  const topRef = useRef(null);

  const [student, setStudent] = useState({
    primaryId: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    dob: "",
    gender: "",
    status: "NEW",
    addressList: [
      {
        streetName1: "",
        streetName2: "",
        city: "",
        state: "",
        zipcode: "",
        country: "",
        type: "HOME",
      },
    ],
    educationDetails: [
      {
        schoolName: "",
        degree: "",
        currentlyStudying: false,
        startDate: "",
        endDate: "",
      },
    ],
    dynamicAttributes: [
      { name: "Nationality", value: "" },
      { name: "Community", value: "" },
      { name: "Language", value: "" },
      { name: "Registration Class", value: "" },
      { name: "How did you know about Velammal", value: "" },
    ],
    company: { primaryId: "companyId", code: "companyId" },
    userRole: "APPLICANT",
    contactPersonList: [
      {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        type: "FATHER",
      }
    ],
    parentList: [
      {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        occupation: "",
        annualIncome: "",
        type: "MOTHER",
      },
    ],
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [loading, setLoading] = useState(false);

  // Scroll management
  useEffect(() => {
    // Immediate scroll to top
    window.scrollTo(0, 0);

    // Additional scroll after short delay to catch any async rendering
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'auto' });
      if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: 'auto' });
      }
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const loadStudentData = async () => {
  try {
    const companyId = selectedBranch.primaryId;

    if (!companyId) {
      setSnackbarMessage("No valid company ID found");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    const response = await dispatch(getUserByCompanyCode(companyId));

    if (!response || response.length === 0) {
      setSnackbarMessage("No student data found");
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }

    const studentData = response[0];

    // Filter out the "Stream" field from dynamicAttributes
    const filteredAttributes = (studentData.dynamicAttributes || []).filter(
      attr => attr.name !== "Stream"
    );

    setStudent({
      firstName: studentData.firstName || "",
      primaryId: studentData.primaryId || "",
      lastName: studentData.lastName || "",
      email: studentData.email || "",
      phoneNumber: studentData.phoneNumber || "",
      dob: studentData.dob || "",
      gender: studentData.gender || "",
      addressList: studentData.addressList || [
        { streetName1: "", city: "", state: "", zipcode: "", country: "" },
      ],
      educationDetails: studentData.educationDetails || [
        { schoolName: "", degree: "" },
      ],
      contactPersonList: studentData.contactPersonList || [
        {
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
          type: "FATHER",
        },
      ],
      parentList: studentData.parentList || [
        {
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
          occupation: "",
          annualIncome: "",
        },
      ],
      dynamicAttributes: filteredAttributes.length > 0 ? filteredAttributes : [
        { name: "Nationality", value: "" },
        { name: "Community", value: "" },
        { name: "Language", value: "" },
        { name: "Registration Class", value: "" },
        { name: "How did you know about Velammal", value: "" },
      ],
    });

    // Scroll again after data loads
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  } catch {
    setSnackbarMessage("An error occurred while fetching student details");
    setSnackbarSeverity("error");
    setOpenSnackbar(true);
  }
};

    loadStudentData();
  }, [dispatch, selectedBranch.primaryId]);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleChange = (field, value) => {
    const fields = field.split('.');
    if (fields.length === 1) {
      setStudent((prevStudent) => ({
        ...prevStudent,
        [field]: value,
      }));
    } else if (fields.length === 2) {
      const [parent, child] = fields;
      setStudent((prevStudent) => ({
        ...prevStudent,
        [parent]: {
          ...prevStudent[parent],
          [child]: value,
        },
      }));
    } else if (fields.length === 3) {
      const [parent, index, child] = fields;
      setStudent((prevStudent) => {
        const updatedArray = [...prevStudent[parent]];
        updatedArray[index][child] = value;
        return {
          ...prevStudent,
          [parent]: updatedArray,
        };
      });
    }
  };

  const handleChangeDynamicAttribute = (idx, value) => {
    setStudent((prevStudent) => {
      const updatedAttributes = [...prevStudent.dynamicAttributes];
      updatedAttributes[idx].value = value;
      return {
        ...prevStudent,
        dynamicAttributes: updatedAttributes,
      };
    });
  };

  const applicantData = JSON.parse(localStorage.getItem("selectedRegistrationClass"));

  const addToShoppingCart = async () => {
    const shoppingCartId = userDetail.account.shoppingCart.primaryId;

    if (!shoppingCartId) {
      return;
    }

    const payload = {
      primaryId: shoppingCartId,
      accountId: userDetail.account.primaryId,
      createdBy: "WEB_UI",
      modifiedBy: "WEB_UI",
      // totalPrice: applicantData.product?.price?.priceAmt || 0,
      productList: [
        {
          quantity: 1,
          scheduledProduct: {
            primaryId: applicantData.primaryId,
            product: applicantData.product,
          }
        }
      ],
      company: {
        primaryId: selectedBranch.primaryId,
      },
    };

    try {
      const response = await addShoppingCartItems(payload);
      if (response) {
        navigate("/cart");
      }
    } catch (error) {
      setSnackbarMessage("An error occurred while adding to the cart");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const handleCart = async () => {
    setLoading(true);
    const company = selectedBranch;

    let scheduledReq = {
      primaryId: applicantData.primaryId || null,
      createdDate: applicantData.createdDate || null,
      createdBy: applicantData.createdBy || null,
      modifiedDate: applicantData.modifiedDate || null,
      modifiedBy: applicantData.modifiedBy || null,
      dynamicAttributes: applicantData.dynamicAttributes || null,
      deleted: applicantData.deleted || false,
      quantity: 1,
      company: company || {},
      // totalPrice: applicantData.product?.price?.priceAmt || 0,
      lineitemHistory: applicantData.lineitemHistory || null,
      product: applicantData.product || null,
      scheduledProduct: {
        primaryId: applicantData.primaryId,
        createdDate: applicantData.createdDate,
        createdBy: applicantData.createdBy,
        modifiedDate: applicantData.modifiedDate,
        modifiedBy: applicantData.modifiedBy,
        dynamicAttributes: applicantData.dynamicAttributes,
        deleted: applicantData.deleted || false,
        period: {
          effectiveStartDate: applicantData.period?.effectiveStartDate || null,
          effectiveEndDate: applicantData.period?.effectiveEndDate || null,
          daysOfSchedule: applicantData.period?.daysOfSchedule || [],
          durationInWeeks: applicantData.period?.durationInWeeks || 0,
          durationInDays: applicantData.period?.durationInDays || 0,
          durationType: applicantData.period?.durationType || null,
          duration: applicantData.period?.duration || 0,
        },
        owners: applicantData.owners || [],
        product: {
          primaryId: applicantData.product?.primaryId || null,
          _id: applicantData.product?._id || null,
          createdDate: applicantData.product?.createdDate || null,
          createdBy: applicantData.product?.createdBy || null,
          modifiedDate: applicantData.product?.modifiedDate || null,
          modifiedBy: applicantData.product?.modifiedBy || null,
          deleted: applicantData.product?.deleted || false,
          name: applicantData.product?.name || "",
          description: applicantData.product?.description || "",
          code: applicantData.product?.code || "",
          deliveryType: applicantData.product?.deliveryType || "UNKNOWN",
          category: applicantData.product?.category || {},
          inStock: applicantData.product?.inStock || false,
          imageUrlsList: applicantData.product?.imageUrlsList || [],
          attributes: applicantData.product?.attributes || {},
          price: applicantData.product?.price || {},
        },
        schedule: {
          primaryId: applicantData.schedule?.primaryId || null,
          _id: applicantData.schedule?._id || null,
          createdDate: applicantData.schedule?.createdDate || null,
          createdBy: applicantData.schedule?.createdBy || null,
          deleted: applicantData.schedule?.deleted || false,
          title: applicantData.schedule?.title || "",
          daysOfSchedule: applicantData.schedule?.daysOfSchedule || [],
          startTime: applicantData.schedule?.startTime || "00:00:00",
          endTime: applicantData.schedule?.endTime || "00:00:00",
          scheduleCode: applicantData.schedule?.scheduleCode || "",
          totalHours: applicantData.schedule?.totalHours || 0,
        },
      },
      status: applicantData.status || "PENDING",
    };

    try {
      await addToShoppingCart(scheduledReq);
    } catch {
      setSnackbarMessage("An error occurred while processing your application");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const productDetails = JSON.parse(localStorage.getItem("selectedRegistrationClass"));
    await handleCart(productDetails);
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-blue-600 to-indigo-800">
      {/* Scroll anchor */}
      <div ref={topRef} className="absolute top-0 left-0 w-full h-0" />

      {/* Fixed Header */}
      <div className="fixed safe-area-top w-full z-50">
        <InstitutionHeader />
      </div>

      {/* Back Button */}
      <div className="fixed left-4 z-40" style={{ top: 'calc(env(safe-area-inset-top, 0px) + 5rem)' }}>
        <BackButton />
      </div>

      {/* Background Animation */}
      <BackgroundComponent />

      {/* Snackbar for notifications */}
      <Snackbar
        autoHideDuration={6000}
        isOpen={openSnackbar}
        message={snackbarMessage}
        onClose={handleCloseSnackbar}
        tone={snackbarSeverity}
      />

      {/* Loading overlay */}
      {loading && <Loading message="Processing your request..." />}

      {/* Main Content */}
      <div className="pt-28 pb-10 px-4"> {/* Extra top padding for fixed header */}
        <div className="max-w-4xl mx-auto bg-white bg-opacity-90 shadow-lg rounded-lg">
          <div className="p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-center mb-6 flex items-center justify-center space-x-4">
              <span>Online Application Registration</span>
              <EditIcon
                className="h-5 w-5 cursor-pointer"
                onClick={() => navigate(`/application-reg?email=${student.email}`)}
              />
            </h2>

            <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6">
              <p className="font-bold">Review Your Application (Ref No: {student.primaryId || "N/A"})</p>
              <p>Please carefully review all the details you've provided to ensure accuracy and completeness.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="border p-4 md:p-6 shadow-md rounded-lg">
                <PersonalInfo
                  student={student}
                  handleChange={handleChange}
                  handleChangeDynamicAttribute={handleChangeDynamicAttribute}
                  disabled
                />
              </div>


              <div className="border p-4 md:p-6 shadow-md rounded-lg">
                <AdditionalInfo
                  student={student}
                  handleChange={handleChange}
                  handleChangeDynamicAttribute={handleChangeDynamicAttribute}
                  disabled
                />
              </div>

              <div className="border p-4 md:p-6 shadow-md rounded-lg">
                <AddressListDetails
                  student={student}
                  handleChange={handleChange}
                  disabled
                />
              </div>

              <div className="border p-4 md:p-6 shadow-md rounded-lg">
                <ContactPersonDetails
                  student={student}
                  handleChange={handleChange}
                  disabled
                />
              </div>
              <div className="border p-4 md:p-6 shadow-md rounded-lg">
                <EducationDetails
                  student={student}
                  handleChange={handleChange}
                  disabled
                />
              </div>


              <div className="border p-4 md:p-6 shadow-md rounded-lg">
                <ParentDetails
                  student={student}
                  handleChange={handleChange}
                  disabled
                />
              </div>

              <div className="flex justify-center pt-4">
                <button
                  type="submit"
                  className="w-full md:w-1/2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition duration-300"
                >
                  Submit & Pay
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicantForm;
